import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useSession, useToast } from '../lib/store';
import { Button, Panel } from '../components/ui';

/**
 * Claiming an account.
 *
 * One screen, three fields, one button. It is a page rather than a modal on
 * purpose: a modal is something the game put in front of you, and the whole
 * premise of guest accounts is that nothing ever does that. You arrive here
 * because you chose to, and "Not now" is always the larger half of the deal.
 *
 * Nothing about the player's game is touched by this — same trainer, same
 * Pokémon, same Groves, same id. The server does an UPDATE on one row.
 */
export function ClaimPage() {
  const { me, claim } = useSession();
  const { push } = useToast();
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [availability, setAvailability] = useState<{ available: boolean; reason: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const alreadyClaimed = me ? !me.account.isGuest : false;

  // Availability, debounced. Purely advisory — the server checks again inside
  // the transaction, because anything checked before a write can be taken
  // between the check and the write.
  useEffect(() => {
    setAvailability(null);
    const candidate = username.trim();
    if (candidate.length < 3) return;

    const timer = window.setTimeout(() => {
      api
        .get<{ available: boolean; reason: string | null }>('/auth/username-available', {
          username: candidate,
        })
        .then(setAvailability)
        .catch(() => setAvailability(null));
    }, 350);

    return () => window.clearTimeout(timer);
  }, [username]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError('Those passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      await claim({ username: username.trim(), email: email.trim(), password });
      push('success', 'Account claimed — your collection is safe.', '★');
      navigate('/');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  if (alreadyClaimed) {
    return (
      <div className="stack">
        <div className="page-head">
          <div>
            <h1 className="page-head__title">Account</h1>
            <div className="page-head__sub">This account is already claimed.</div>
          </div>
        </div>
        <Panel title="◆ All set">
          <p className="tiny muted">
            You can sign in as <strong>{me?.user.username}</strong> from any device. Change your
            username in Settings.
          </p>
          <div style={{ marginTop: 'var(--gap-sm)' }}>
            <Button onClick={() => navigate('/settings')}>Go to Settings</Button>
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Claim your account</h1>
          <div className="page-head__sub">
            Keep {me?.profile.trainerName ?? 'your trainer'} — and everything they've collected.
          </div>
        </div>
      </div>

      <div
        className="grid"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', alignItems: 'start' }}
      >
        <Panel title="◈ Choose your details">
          <form onSubmit={onSubmit} className="stack">
            <div className="field">
              <label className="field__label" htmlFor="claim-username">
                Username
              </label>
              <input
                id="claim-username"
                className="input"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                autoComplete="username"
                required
                minLength={3}
                maxLength={18}
                placeholder="Letters, numbers and underscores"
              />
              <span className="tiny muted">
                {availability === null
                  ? 'This is how other trainers will find you.'
                  : availability.available
                    ? '✓ Available.'
                    : (availability.reason ?? 'Not available.')}
              </span>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="claim-email">
                Email
              </label>
              <input
                id="claim-email"
                className="input"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                required
                placeholder="you@example.com"
              />
              <span className="tiny muted">Used to get back in if you forget your password.</span>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="claim-password">
                Password
              </label>
              <input
                id="claim-password"
                className="input"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                required
                minLength={8}
                placeholder="At least 8 characters"
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="claim-confirm">
                Confirm password
              </label>
              <input
                id="claim-confirm"
                className="input"
                type="password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                autoComplete="new-password"
                required
                minLength={8}
              />
            </div>

            {error && <div className="error-text">{error}</div>}

            <Button type="submit" variant="primary" size="lg" block loading={busy}>
              Claim account
            </Button>

            <div className="center">
              <button type="button" className="linkbtn tiny" onClick={() => navigate(-1)}>
                Not now — keep playing
              </button>
            </div>
          </form>
        </Panel>

        <div className="stack">
          <Panel title="◇ What changes">
            <ul className="stack-sm tiny" style={{ margin: 0, paddingLeft: '1.1em' }}>
              <li>
                You'll be able to <strong>sign in from any device</strong> — phone, laptop, anywhere.
              </li>
              <li>
                Your Pokémon, Groves, eggs, friends, Guild, Bazaar stall and achievements all stay
                exactly as they are. Nothing moves.
              </li>
              <li>
                Choosing a username here is <strong>free</strong> and does not use your one free
                rename — {me?.account.temporaryName ?? 'your trainer name'} was assigned, not chosen.
              </li>
            </ul>
          </Panel>

          <Panel title="⚠ Why it matters">
            <p className="tiny muted" style={{ margin: 0 }}>
              Guest accounts are stored on this device. If you clear your browser data, switch
              browsers or lose this device, there is no way to recover the account —
              nothing else identifies it. Claiming is the only thing that changes that.
            </p>
          </Panel>
        </div>
      </div>
    </div>
  );
}
